// src/api/wordbookApi.ts
import { apiClient } from './axios';
import {Wordbook, WordbookDetail} from '../types/wordbook';
import {CreateWordbookRequest, CreateWordbookResponse} from '../models/wordbook/createWordbookRequest';
import { UpdateWordbookRequest } from '../models/wordbook/updateWordbookRequest';
import { WorkbookFilterParams } from '../models/wordbook/workbookFilterParams';
import { PaginationData } from '../models/paginationData';
import {
    WorkbookStatsResponse,
    WorkbookOverviewStatsResponse,
    LanguageStatsResponse,
    BulkDeleteWordbooksResponse,
    BulkMoveToGroupResponse,
    BulkUpdateVisibilityResponse
} from '../models/wordbook/wordbookResponses';

// Wordbook API
export const wordbookApi = {
    // 목록 조회
    getList: async (params: WorkbookFilterParams) => {
        const { page, size, sortBy, sortDirection, ...filters } = params;

        return apiClient.get<PaginationData<Wordbook>>('/admin/wordbooks', {
            page: page - 1,
            size,
            sortBy: sortBy || 'createdAt',
            sortDirection: sortDirection || 'desc',
            ...filters,
        });
    },

    // 단건 조회
    getOne: async (id: number) => {
        return apiClient.get<WordbookDetail>(`/admin/wordbooks/${id}`);
    },

    // 생성
    create: async (data: CreateWordbookRequest) => {
        return apiClient.post<CreateWordbookResponse>(`/admin/wordbooks`, data);
    },

    // 수정
    update: async (id: number, data: UpdateWordbookRequest) => {
        return apiClient.putNoData(`/admin/wordbooks/${id}`, data);
    },

    // 삭제 (소프트 삭제)
    delete: async (id: number) => {
        return apiClient.deleteNoData(`/admin/wordbooks/${id}`);
    },

    // 영구 삭제
    permanentDelete: async (id: number) => {
        return apiClient.deleteNoData(`/admin/wordbooks/${id}/permanent`);
    },

    // 복원
    restore: async (id: number) => {
        return apiClient.postNoData(`/admin/wordbooks/${id}/restore`);
    },

    // 노출 토글
    toggleVisibility: async (id: number) => {
        return apiClient.patchNoData(`/admin/wordbooks/${id}/visibility`);
    },

    // 그룹 변경
    changeGroup: async (id: number, groupId: number | null) => {
        return apiClient.patchNoData(`/admin/wordbooks/${id}/group`, { groupId });
    },

    // 순서 변경
    updateSortOrder: async (id: number, sortOrder: number) => {
        return apiClient.patchNoData(`/admin/wordbooks/${id}/sort-order`, { sortOrder });
    },

    // 통계 조회
    getStats: async (id: number) => {
        return apiClient.get<WorkbookStatsResponse>(`/admin/wordbooks/${id}/stats`);
    },

    // 일괄 삭제
    bulkDelete: async (wordbookIds: number[]) => {
        return apiClient.post<BulkDeleteWordbooksResponse>(
            '/admin/wordbooks/bulk-delete',
            { wordbookIds }
        );
    },

    // 일괄 그룹 이동
    bulkMoveToGroup: async (wordbookIds: number[], targetGroupId: number | null) => {
        return apiClient.post<BulkMoveToGroupResponse>(
            '/admin/wordbooks/bulk-move-group',
            { wordbookIds, targetGroupId }
        );
    },

    // 일괄 노출 설정
    bulkUpdateVisibility: async (wordbookIds: number[], visibility: string) => {
        return apiClient.post<BulkUpdateVisibilityResponse>(
            '/admin/wordbooks/bulk-visibility',
            { wordbookIds, visibility }
        );
    },

    // 전체 통계
    getOverviewStats: async () => {
        return apiClient.get<WorkbookOverviewStatsResponse>('/admin/wordbooks/stats/overview');
    },

    // 언어별 통계
    getLanguageStats: async () => {
        return apiClient.get<LanguageStatsResponse>('/admin/wordbooks/stats/by-language');
    },
};
